import type { MouseEventHandler, ReactNode } from 'react'

import { cn } from '@gto/ui/cn'

import {
  getRenderableModalBlock,
  type ModalBlockRenderOptions,
  type RenderableModalBlock,
} from './modal-block-renderability'
import type { ModalBlockPayload, ModalBlockSpacing } from './modal-blocks/modal-block-types'
import { ModalBulletsBlock } from './modal-blocks/modal-bullets-block'
import { ModalCardBlock } from './modal-blocks/modal-card-block'
import { ModalCtaBlock } from './modal-blocks/modal-cta-block'
import { ModalHeadingBlock } from './modal-blocks/modal-heading-block'
import { ModalHeroBlock } from './modal-blocks/modal-hero-block'
import { ModalImageBlock } from './modal-blocks/modal-image-block'
import { ModalParagraphBlock } from './modal-blocks/modal-paragraph-block'
import { ModalRowBlock } from './modal-blocks/modal-row-block'
import { ModalVideoBlock } from './modal-blocks/modal-video-block'

export type ModalCardProps = {
  /** Admin-only. See `ModalBlockRenderOptions`. */
  allowLinklessCta?: boolean
  /** Admin-only. See `ModalBlockRenderOptions`. */
  allowStagedMedia?: boolean
  blocks: readonly ModalBlockPayload[]
  emptyFallback?: ReactNode
  onCtaClick?: MouseEventHandler<HTMLAnchorElement>
}

/**
 * The gap ABOVE each block. `default` is the rhythm every stored modal has had
 * since the card was a flex `gap`, so a block that never authored one is
 * unchanged. A Record keyed by the union, so a step the contract adds fails to
 * compile here rather than rendering with no margin at all.
 */
const SPACING_CLASS = {
  default: 'mt-10 sm:mt-8',
  small: 'mt-3 sm:mt-2',
  none: 'mt-0',
} as const satisfies Record<ModalBlockSpacing, string>

/**
 * The frame each top-level block sits in.
 *
 * Every frame is a column that centres its block, so a block sees exactly the
 * flex parent it saw when the card had no frames. A hero also bleeds past the
 * dialog's padding: `-mx-4` undoes `Dialog.Popup`'s `p-4`, and when the hero opens
 * the modal `-mt-8` also undoes `Dialog.Content`'s `py-4` above it. The admin
 * preview reproduces both paddings, so the same numbers hold on both surfaces.
 */
// `empty` never reaches the top level, so it has no spacing to read.
const spacingOf = (block: RenderableModalBlock): ModalBlockSpacing =>
  block.type === 'empty' ? 'default' : (block.spacing ?? 'default')

const blockFrameClass = (block: RenderableModalBlock, index: number): string =>
  cn(
    'flex flex-col items-center self-stretch',
    index > 0 && SPACING_CLASS[spacingOf(block)],
    block.type === 'hero' && '-mx-4 sm:-mx-2',
    block.type === 'hero' && index === 0 && '-mt-8 sm:-mt-6',
  )

const renderBlock = (
  block: RenderableModalBlock,
  onCtaClick?: MouseEventHandler<HTMLAnchorElement>,
  centerOnMobile?: boolean,
): ReactNode => {
  switch (block.type) {
    case 'heading':
      return (
        <ModalHeadingBlock
          align={block.align}
          centerOnMobile={centerOnMobile}
          highlight={block.highlight}
          size={block.size}
          text={block.text}
        />
      )
    case 'paragraph':
      return (
        <ModalParagraphBlock
          align={block.align}
          centerOnMobile={centerOnMobile}
          highlight={block.highlight}
          style={block.style}
          text={block.text}
        />
      )
    case 'bullets':
      return (
        <ModalBulletsBlock
          align={block.align}
          centerOnMobile={centerOnMobile}
          items={block.items}
        />
      )
    case 'image':
      return (
        <ModalImageBlock
          align={block.align}
          centerOnMobile={centerOnMobile}
          url={block.url}
          width={block.width}
        />
      )
    case 'video':
      return (
        <ModalVideoBlock
          align={block.align}
          centerOnMobile={centerOnMobile}
          url={block.url}
          width={block.width}
        />
      )
    case 'hero':
      return <ModalHeroBlock url={block.url} />
    case 'cta':
      return (
        <ModalCtaBlock
          link={block.link}
          style={block.style}
          text={block.text}
          onClick={onCtaClick}
        />
      )
    case 'card':
      return (
        <ModalCardBlock
          heading={block.heading}
          style={block.style}
          text={block.text}
        />
      )
    case 'empty':
      return null
    case 'row':
      return (
        <ModalRowBlock {...block}>
          {block.children.map((child) =>
            renderBlock(child, onCtaClick, block.mobile_align === 'center'),
          )}
        </ModalRowBlock>
      )
  }
}

export const ModalCard = ({
  allowLinklessCta,
  allowStagedMedia,
  blocks,
  emptyFallback = null,
  onCtaClick,
}: ModalCardProps) => {
  const options: ModalBlockRenderOptions = { allowLinklessCta, allowStagedMedia }
  const renderedBlocks = blocks.flatMap((block) => {
    const renderable = getRenderableModalBlock(block, true, options)

    return renderable === undefined ? [] : [renderable]
  })

  if (renderedBlocks.length === 0) {
    return emptyFallback
  }

  return (
    <div className="flex w-full flex-col items-center text-center">
      {renderedBlocks.map((block, index) => (
        <div
          // eslint-disable-next-line @eslint-react/no-array-index-key -- ordered API blocks have no stable id
          key={index}
          className={blockFrameClass(block, index)}
          data-tst="announcement-modal-block"
        >
          {renderBlock(block, onCtaClick)}
        </div>
      ))}
    </div>
  )
}
